import type { JSX } from 'react';
import { Link } from 'react-router';

import { NAV_ITEMS, SECONDARY_NAV_ITEMS } from '@/app/navigation';
import { ExternalLinkIcon, LocationIcon } from '@/components/ui/icons';
import { profile } from '@/content/profile';

const currentYear = new Date().getFullYear();

export function Footer(): JSX.Element {
  return (
    <footer className="border-t border-line bg-surface-muted/40">
      <div className="shell-container grid gap-10 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:py-16">
        <div className="lg:col-span-2">
          <p className="text-base font-semibold text-text">{profile.name}</p>
          <p className="mt-1 text-sm text-muted">{profile.role}</p>
          <p className="mt-4 flex items-center gap-2 text-sm text-muted">
            <LocationIcon width={16} height={16} />
            {profile.location}
          </p>
          <p className="mt-4 max-w-md text-sm text-muted">
            React, TypeScript and Next.js specialist. Available for conversations about senior
            frontend and full-stack roles.
          </p>
        </div>

        <nav aria-label="Footer">
          <h2 className="text-sm font-semibold text-text">Site</h2>
          <ul className="mt-4 flex flex-col gap-2">
            {[...NAV_ITEMS, ...SECONDARY_NAV_ITEMS].map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="inline-flex min-h-8 items-center text-sm text-muted no-underline hover:text-text hover:underline"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h2 className="text-sm font-semibold text-text">Contact</h2>
          <ul className="mt-4 flex flex-col gap-2">
            {profile.links.map((link) => (
              <li key={link.id}>
                <a
                  href={link.href}
                  className="inline-flex min-h-8 items-center gap-1.5 wrap-anywhere text-sm text-muted no-underline hover:text-text hover:underline"
                  {...(link.external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
                >
                  {link.label}
                  {link.external ? (
                    <>
                      <ExternalLinkIcon width={14} height={14} />
                      <span className="sr-only">(opens in a new tab)</span>
                    </>
                  ) : null}
                </a>
              </li>
            ))}
            {profile.showPhone ? (
              <li>
                <a
                  href={`tel:${profile.phone.replace(/\s+/g, '')}`}
                  className="inline-flex min-h-8 items-center text-sm text-muted no-underline hover:text-text hover:underline"
                >
                  {profile.phone}
                </a>
              </li>
            ) : null}
          </ul>
        </div>
      </div>

      <div className="border-t border-line">
        <div className="shell-container flex flex-col gap-2 py-6 text-xs text-muted sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {currentYear} {profile.name}. All rights reserved.
          </p>
          <p>Built with React, TypeScript, Vite and NestJS.</p>
        </div>
      </div>
    </footer>
  );
}
